/**
 * Mindtickle series catalog for the journey map builder.
 *
 * Courses are modelled as Mindtickle *series*, which is the unit a learner is enrolled in
 * and the unit Snowflake reports completion against. Snowflake is the only source: nothing
 * here is authored by hand, so a series that has been retired upstream simply stops
 * appearing in the picker (items already pointing at it keep their stored launch URL).
 *
 * `snowflake/mindtickle/series/` is a compact projection of the much larger `structure/`
 * payload — one row per series with the entry module already resolved into a launch URL.
 */
import { fetchItems } from '../queries';

export type MindtickleSeries = {
  series_id: string;
  series_name: string;
  series_description?: string;
  series_state?: string;
  series_creation_time?: string | null;
  series_created_by_name?: string;
  series_is_public?: boolean | null;
  total_published_modules?: number | null;
  module_count?: number;
  /**
   * Mindtickle consumes a series through one of its modules, so the launch URL is built
   * from the series' first module. Null when the series has no modules yet.
   */
  entry_module_id?: string | null;
  entry_module_name?: string;
  launch_url?: string | null;
};

export const MINDTICKLE_SERIES_ENDPOINT = 'snowflake/mindtickle/series/';

type FetchOptions = {
  search?: string;
  limit?: number;
  useCache?: boolean;
  signal?: AbortSignal;
};

/**
 * Snowflake queries are slow and the catalog changes at most nightly, so reads default to
 * the server-side cache. Pass `useCache: false` for an explicit refresh.
 */
export async function fetchMindtickleSeries(
  options: FetchOptions = {},
): Promise<MindtickleSeries[]> {
  const { search, limit, useCache = true, signal } = options;

  const params: Record<string, any> = { use_cache: useCache ? 'true' : 'false' };

  if (search && search.trim()) params.search = search.trim();
  if (limit) params.limit = limit;

  const payload = await fetchItems(MINDTICKLE_SERIES_ENDPOINT, params, {
    includePageSize: false,
    signal,
  });

  // fetchItems unwraps `items` for us, but tolerate a bare array or an empty response.
  if (Array.isArray(payload)) return payload as MindtickleSeries[];
  if (Array.isArray(payload?.items)) return payload.items as MindtickleSeries[];

  return [];
}

export function mindtickleSeriesLabel(series: MindtickleSeries): string {
  const name = String(series?.series_name || '').trim();

  return name || `Series ${series?.series_id || ''}`.trim();
}

/**
 * Secondary line for the picker: how much content the series holds and who owns it.
 * Kept short so long series names are not pushed out of a dropdown row.
 */
export function mindtickleSeriesSubLabel(series: MindtickleSeries): string {
  const moduleCount = Number(series?.total_published_modules ?? series?.module_count ?? 0);

  const parts: string[] = [];

  if (moduleCount > 0) {
    parts.push(`${moduleCount} module${moduleCount === 1 ? '' : 's'}`);
  }

  if (series?.series_created_by_name) parts.push(String(series.series_created_by_name));

  return parts.join(' · ');
}

export function findMindtickleSeries(
  catalog: MindtickleSeries[],
  seriesId?: string | null,
): MindtickleSeries | undefined {
  if (!seriesId) return undefined;

  const wanted = String(seriesId);

  return catalog.find((series) => String(series.series_id) === wanted);
}

/**
 * A series with no modules has no consumable URL, so it cannot be launched and should not
 * be selectable as a journey map step.
 */
export function isMindtickleSeriesLaunchable(series?: MindtickleSeries | null): boolean {
  return Boolean(series?.launch_url);
}
